import CustomPageNotification from "@components/layouts/CustomPageNotification";
import {
  Alert,
  Box,
  Button,
  Group,
  NumberInput,
  Select,
  Stack,
  TextInput,
} from "@mantine/core";
import { logError } from "api/diagnostic.service";
import { addUser } from "api/user.service";
import { useState } from "react";

type Role = "Administrators" | "Customers" | "Staff";

interface IAddUserPageProps {
  onCancelled?: () => void;
  onUserAdded?: () => void;
}

const AddUserPage: React.FC<IAddUserPageProps> = ({
  onCancelled,
  onUserAdded,
}) => {
  const [username, setUsername] = useState("");
  const [role, setRole] = useState<Role>("Staff");
  const [usercode, setUsercode] = useState<string | number | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (!username.trim()) {
      setError("Username is required.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(username.trim())) {
      setError("Please enter a valid email address.");
      return;
    }

    if (role === "Customers" && !usercode) {
      setError("Usercode is required for customers.");
      return;
    }

    const newUser = {
      username: username.trim(),
      role,
      usercode: usercode || undefined,
    };

    try {
      const response = await addUser(
        newUser.username,
        newUser.role,
        Number(newUser.usercode),
      );
      if (response.success === false) {
        setError(response.error);
        return;
      }

      onUserAdded?.();
    } catch (err: any) {
      logError(err);
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError("Unexpected error occurred.");
      }

      return;
    } finally {
      setUsername("");
      setRole("Staff");
      setUsercode(undefined);
    }
  };

  return (
    <Box>
      <CustomPageNotification
        title="Add User"
        message="The user will be sent an email with their login details"
      />

      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <TextInput
            label="Username (Email)"
            placeholder="Enter username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
          <Select
            label="Role"
            value={role}
            onChange={(value) => setRole(value as Role)}
            data={[
              { value: "Administrators", label: "Admin" },
              { value: "Customers", label: "Customer" },
              { value: "Staff", label: "Staff" },
            ]}
            comboboxProps={{
              withinPortal: true,
              zIndex: 3000,
              transitionProps: { duration: 0 }, // prevents animation bugs
            }}
          />

          <NumberInput
            label="Usercode"
            placeholder="Customer code"
            value={usercode}
            onChange={(value) => setUsercode(value ? String(value) : "")}
            required={role === "Customers"}
            disabled={role !== "Customers"}
          />

          {error && (
            <Alert color="red" variant="light">
              {error}
            </Alert>
          )}

          {success && (
            <Alert color="green" variant="light">
              {success}
            </Alert>
          )}

          <Group justify="flex-end" mt="md">
            <Button type="submit">Add User</Button>
            <Button
              onClick={() => {
                onCancelled?.();
              }}
            >
              Cancel
            </Button>
          </Group>
        </Stack>
      </form>
    </Box>
  );
};

export default AddUserPage;
